import { useAppContext } from '@common/contexts/AppContext';
import { trackRemoveFromCart } from '@features/analytics';
import { updateBasket } from '@store/basket/actions';
import { isBasketItemExpired } from '@store/basket/utils/isBasketItemExpired';
import { entitySelectors } from '@store/entity/selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Item } from 'shared-types';
import { BasketDetails } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { RoomUpgrade } from 'shared-types/Rate';
import { roomIdsInBasket, toUpgradedRoomItem } from './roomUpgradeItems';
import { markRoomUpgraded } from './upgradedRooms';
import { findRoomUpgrade, upgradeAvailabilityQuery } from './useBasketRoomUpgradeOffers';

type AppState = { basket: { basketDetails: BasketDetails } } & Record<string, unknown>;

export type UpgradeOutcome =
  | 'upgraded'
  | 'unavailable'
  | 'changed'
  | 'failed'
  | 'originalNotRemoved';

const sameId = (a: Item['id'], b: Item['id']) => String(a) === String(b);

const hasItem = (items: Item[], id: Item['id']) => items.some((item) => sameId(item.id, id));

const isSameOffer = (latest: RoomUpgrade, accepted: RoomUpgrade) =>
  latest.id === accepted.id &&
  latest.priceDifference?.amount === accepted.priceDifference?.amount &&
  latest.priceDifference?.currencyCode === accepted.priceDifference?.currencyCode &&
  latest.pricePerStay?.amount === accepted.pricePerStay?.amount &&
  latest.pricePerStay?.currencyCode === accepted.pricePerStay?.currencyCode;

const findAddedUpgrade = (before: Item[], after: Item[], upgradeRoomId: string) =>
  after.find(
    (candidate) =>
      !hasItem(before, candidate.id) &&
      candidate.type === 'rooms' &&
      candidate.bookable?.ids?.roomId === upgradeRoomId
  );

export const useUpgradeBasketRoom = (language: string) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, AnyAction>>();
  const store = useStore<AppState>();
  const queryClient = useQueryClient();
  const { siteId } = useAppContext();
  const property = useSelector(entitySelectors.getProperty) as Property;
  const [isUpgrading, setIsUpgrading] = useState(false);
  const heldUpgrades = useRef(new Map<string, Item['id']>());

  const currentBasket = useCallback(() => store.getState().basket.basketDetails, [store]);

  const patchItems = useCallback(
    (items: unknown[]) => {
      const basket = currentBasket();
      return dispatch(
        updateBasket(
          property.id,
          basket.id,
          language,
          { promoCode: basket.promoCode, items },
          true,
          true
        )
      );
    },
    [currentBasket, property, language, dispatch]
  );

  const currentItems = useCallback(() => {
    const items = currentBasket()?.items;
    return Array.isArray(items) ? items : [];
  }, [currentBasket]);

  const releaseOriginal = useCallback(
    async (item: Item, addedId: Item['id']): Promise<UpgradeOutcome> => {
      const key = String(item.id);
      heldUpgrades.current.set(key, addedId);
      await patchItems(currentItems().filter((candidate) => !sameId(candidate.id, item.id)));
      if (hasItem(currentItems(), item.id)) return 'originalNotRemoved';
      heldUpgrades.current.delete(key);
      trackRemoveFromCart(item);
      return 'upgraded';
    },
    [currentItems, patchItems]
  );

  const upgradeRoom = useCallback(
    async (item: Item, accepted: RoomUpgrade): Promise<UpgradeOutcome> => {
      const key = String(item.id);
      setIsUpgrading(true);
      try {
        if (isBasketItemExpired(item)) return 'unavailable';

        const before = currentItems();
        const heldId = heldUpgrades.current.get(key);
        if (heldId != null && hasItem(before, heldId)) return await releaseOriginal(item, heldId);
        heldUpgrades.current.delete(key);

        const latest = await queryClient.fetchQuery({
          ...upgradeAvailabilityQuery(item, siteId),
          staleTime: 0,
        });
        const upgrade = findRoomUpgrade(item, latest, roomIdsInBasket(before));
        if (!upgrade) return 'unavailable';
        if (!isSameOffer(upgrade, accepted)) return 'changed';

        await patchItems([...before, toUpgradedRoomItem(item, upgrade.id)]);
        const afterAdd = currentItems();
        const added = findAddedUpgrade(before, afterAdd, upgrade.id);
        if (!added) return 'failed';
        if (added.bookable?.status === 'failed') {
          await patchItems(afterAdd.filter((candidate) => !sameId(candidate.id, added.id)));
          return 'failed';
        }

        const basketId = currentBasket()?.id;
        markRoomUpgraded(basketId, item.id);
        markRoomUpgraded(basketId, added.id);
        return await releaseOriginal(item, added.id);
      } catch {
        const items = currentItems();
        const heldId = heldUpgrades.current.get(key);
        const isHeld = heldId != null && hasItem(items, heldId);
        if (isHeld && hasItem(items, item.id)) return 'originalNotRemoved';
        heldUpgrades.current.delete(key);
        return isHeld ? 'upgraded' : 'failed';
      } finally {
        setIsUpgrading(false);
      }
    },
    [currentBasket, currentItems, patchItems, queryClient, releaseOriginal, siteId]
  );

  return { upgradeRoom, isUpgrading };
};
